import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { Locker } from "@/api/eawbApi";
import { DIGITAL_PICTURE_PRODUCT_ID, clampQuantity } from "@/domain/product";

export interface CartProduct {
  id: string;
  name: string;
  price: number;
  qty: number;
  color?: string;
  size?: string;
  pictureUrl?: string;
  original_filename?: string;
  pictureId?: string; // identifies the specific photo for photo-based products
  cloudinary_public_id?: string; // underlying Cloudinary asset public id (for transformations / reuse)
}

interface CartState {
  items: CartProduct[];
  selectedLocker: Locker | null;
}

const initialState: CartState = {
  items: [],
  selectedLocker: null,
};

export const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    addToCart: (state, action: PayloadAction<CartProduct>) => {
      const { id, color, size, pictureId } = action.payload; // cloudinary_public_id ignored for uniqueness
      const existing = state.items.find(
        (item) =>
          item.id === id &&
          item.color === color &&
          item.size === size &&
          item.pictureId === pictureId
      );
      if (existing) {
        // Enforce max quantity 1 ONLY for the specific digital product id
        if (id === DIGITAL_PICTURE_PRODUCT_ID) {
          existing.qty = 1;
        } else {
          existing.qty += action.payload.qty;
        }
      } else {
        state.items.push({
          ...action.payload,
          qty: clampQuantity(action.payload.id, action.payload.qty),
        });
      }
    },
    removeFromCart: (
      state,
      action: PayloadAction<{
        id: string;
        color?: string;
        size?: string;
        pictureId?: string;
        cloudinary_public_id?: string;
      }>
    ) => {
      state.items = state.items.filter(
        (item) =>
          !(
            item.id === action.payload.id &&
            item.color === action.payload.color &&
            item.size === action.payload.size &&
            item.pictureId === action.payload.pictureId
          )
      );
    },
    updateQuantity: (
      state,
      action: PayloadAction<{
        id: string;
        qty: number;
        color?: string;
        size?: string;
        pictureId?: string;
        cloudinary_public_id?: string;
      }>
    ) => {
      const { id, qty, color, size, pictureId } = action.payload; // ignore cloudinary_public_id for lookup
      const item = state.items.find(
        (item) =>
          item.id === id &&
          item.color === color &&
          item.size === size &&
          item.pictureId === pictureId
      );
      if (item) {
        item.qty = clampQuantity(id, qty);
      }
    },
    emptyCart: (state) => {
      state.items = [];
      state.selectedLocker = null;
    },
    /** Replace cart prices with the catalogue's (sent back by /api/checkout on PRICE_CHANGED). */
    updatePrices: (state, action: PayloadAction<Record<string, number>>) => {
      for (const item of state.items) {
        const price = action.payload[item.id];
        if (typeof price === "number") item.price = price;
      }
    },
    setSelectedLocker: (state, action: PayloadAction<Locker | null>) => {
      state.selectedLocker = action.payload;
    },
    clearSelectedLocker: (state) => {
      state.selectedLocker = null;
    },
  },
});

// Selectors
import type { RootState } from "@/store";
import { createSelector } from "@reduxjs/toolkit";

export const selectCartItems = (state: RootState) => state.cart.items;

export const selectCartItemCount = createSelector(
  [selectCartItems],
  (items: CartProduct[]) =>
    items.reduce((sum: number, item: CartProduct) => sum + item.qty, 0)
);

export const selectCartTotalPrice = createSelector(
  [selectCartItems],
  (items: CartProduct[]) =>
    items.reduce(
      (sum: number, item: CartProduct) => sum + item.qty * item.price,
      0
    )
);

export const selectSelectedLocker = (state: RootState) => state.cart.selectedLocker;
